// Association Plan Years drawer — Slice A (Dave 2026-02-17).
//
// Lists every plan year for this association with a Reactivate action on
// each non-active row. Active row is chip-tagged with a coral dot; all
// prior years remain permanently viewable and reactivatable — no
// arbitrary rollback window.
//
// Design decisions per Dave (A1 · B1 · C1):
//   * A1 — non-active plan years are strict read-only. To edit, you
//          must Reactivate.
//   * B1 — Association Admin (or Firm/Platform Admin) may Reactivate.
//   * C1 — no auto-propagation warning; each year is edited on its own.
//
// Chrome mirrors AssociationMembersDrawer / AuditFeedDrawer for
// consistency (navy header, coral-2-navy-left border, sky-wash rows).
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, RotateCw, RotateCcw, AlertCircle, Trash2, Calendar } from "lucide-react";
import { useAuth, isPlatformAdmin } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PlanYearsDrawer({
  association,
  canEdit,
  onClose,
  onChanged,
  onNewPlanYear,
}) {
  const aid = association?.aid;
  const { user } = useAuth();
  const isPlatform = isPlatformAdmin(user);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true); // 2026-10-08 (Dave) — start true so the first paint shows "Loading…" instead of flashing "No plan years yet." before the fetch kicks off
  const [confirmPy, setConfirmPy] = useState(null); // {plan_year_id, year} pending confirm
  const [reactivating, setReactivating] = useState(false);
  const [flash, setFlash] = useState(""); // success/toast text

  // 2026-02-23 (Dave) — Platform-Admin-only hard delete. Confirms via a
  // separate dialog so the destructive path can never be one-click.
  const [confirmDeletePy, setConfirmDeletePy] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/associations/${aid}/plan-years`);
      const list = res.data?.plan_years || [];
      setRows(list);
      // Screen loaded before the active year changed elsewhere → refresh it.
      const active = list.find((r) => r.status === "active");
      if (active && association?.plan_year_id && active.plan_year_id !== association.plan_year_id) onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load plan years.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aid) load();
  }, [aid]);

  const activeYear = rows.find((r) => r.status === "active")?.year;

  const doReactivate = async () => {
    if (!confirmPy) return;
    setReactivating(true);
    setErr("");
    try {
      await axios.post(
        `${API}/associations/${aid}/plan-years/${confirmPy.plan_year_id}/reactivate`,
      );
      setFlash(`Plan Year ${confirmPy.year} is now active.`);
      setConfirmPy(null);
      await load();
      onChanged?.();
      // Slice B (Dave 2026-02-17) — notify the TopBar Plan-Year picker
      // so its dropdown reflects the new active/archived states without
      // the user having to switch associations.
      window.dispatchEvent(new Event("refresh-plan-years"));
      // Fade the flash after a beat.
      setTimeout(() => setFlash(""), 3500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reactivate failed.");
    } finally {
      setReactivating(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDeletePy) return;
    setDeleting(true);
    setErr("");
    try {
      const res = await axios.delete(
        `${API}/associations/${aid}/plan-years/${confirmDeletePy.plan_year_id}`,
      );
      const promoted = res.data?.promoted_year;
      setFlash(
        `Plan Year ${confirmDeletePy.year} deleted.` +
        (promoted ? ` Plan Year ${promoted} promoted to active.` : ""),
      );
      setConfirmDeletePy(null);
      await load();
      onChanged?.();
      window.dispatchEvent(new Event("refresh-plan-years"));
      setTimeout(() => setFlash(""), 3500);
    } catch (e) {
      // 2026-02-23 (Dave) — surface any backend detail or fallback to
      // a generic message. Close the confirm dialog so the drawer's
      // error banner is visible (dialog is z-60, drawer is z-50).
      setErr(
        e?.response?.data?.detail
          || e?.message
          || "Delete failed. Check server logs for details."
      );
      setConfirmDeletePy(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,42,68,0.35)" }}
      onClick={onClose}
      data-testid="assoc-planyears-drawer"
    >
      <div
        className="bg-white w-[560px] max-w-[92vw] max-h-[85vh] shadow-2xl border border-navy rounded-[6px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-navy text-white px-4 py-3 rounded-t-[6px]">
          <div className="font-semibold text-sm">
            Plan Years &middot; {association?.name}
          </div>
          <div className="flex items-center gap-2">
            {/* 2026-02-27 (Dave) — "New Plan Year" moved off the
                Association pill row and into this drawer's header
                since the two features are logically paired (start a
                new plan year vs. review/reactivate an existing one).
                Only editable users see the button. */}
            {canEdit && onNewPlanYear && (
              <button
                onClick={onNewPlanYear}
                title="Start a new plan year"
                className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] bg-white/10 hover:bg-white/25 border border-white/30 rounded-[3px] transition-colors"
                data-testid="assoc-planyears-new"
                data-write-action="true"
              >
                <Calendar size={11} /> New Plan Year
              </button>
            )}
            <button
              onClick={load}
              disabled={loading}
              title="Refresh"
              className="text-white/85 hover:text-white disabled:opacity-40"
              data-testid="assoc-planyears-refresh"
            >
              <RotateCw size={14} />
            </button>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
              data-testid="assoc-planyears-close"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Intro strip */}
        <div className="px-4 py-2 border-b border-rule bg-[#F5F9FE] text-[11px] text-slate leading-relaxed">
          Every plan year for this association is preserved permanently.
          Only one plan year is <b className="text-navy">Active</b> at a
          time; every other year is closed but fully viewable. To edit
          a closed year&apos;s numbers, click{" "}
          <b className="text-navy">Reactivate</b> &mdash; the currently
          active year will be archived in its place.
        </div>

        {/* Flash / error */}
        {flash && (
          <div
            className="px-4 py-2 text-[12px] border-b border-rule"
            style={{ background: "#E7F3E7", color: "#1E5F1E" }}
            data-testid="assoc-planyears-flash"
          >
            {flash}
          </div>
        )}
        {err && (
          <div
            className="px-4 py-2 text-[12px] border-b border-rule flex items-center gap-2"
            style={{ background: "#FCEDEA", color: "#9A2E1A" }}
            data-testid="assoc-planyears-error"
          >
            <AlertCircle size={13} />
            {err}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading && rows.length === 0 ? (
            <div className="px-4 py-6 text-slate text-[12px]">Loading&hellip;</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-6 text-slate text-[12px]">
              No plan years yet.
            </div>
          ) : (
            <table className="w-full text-[12px]" data-testid="assoc-planyears-table">
              <thead>
                <tr
                  className="text-left text-slate uppercase tracking-wider text-[10px]"
                  style={{ background: "#F5F9FE" }}
                >
                  <th className="px-4 py-2 font-semibold">Year</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Created</th>
                  <th className="px-4 py-2 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((py) => {
                  const isActive = py.status === "active";
                  return (
                    <tr
                      key={py.plan_year_id}
                      className="border-t border-rule"
                      data-testid={`assoc-planyears-row-${py.year}`}
                    >
                      <td className="px-4 py-2 font-mono font-semibold text-navy">
                        {py.year}
                      </td>
                      <td className="px-4 py-2">
                        {isActive ? (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider"
                            style={{
                              background: "#F5F9FE",
                              color: "#1C4E80",
                              border: "1px solid #88BFDE",
                            }}
                          >
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full"
                              style={{ background: "#EA6A47" }}
                            />
                            Active
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider"
                            style={{
                              background: "#FAFBFC",
                              color: "#6B7A8F",
                              border: "1px solid #C9D6E2",
                            }}
                          >
                            Closed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate">
                        {formatDate(py.created_at)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          {isActive ? (
                            <span className="text-slate/60 text-[11px] italic">
                              Currently active
                            </span>
                          ) : canEdit ? (
                            <button
                              onClick={() =>
                                setConfirmPy({
                                  plan_year_id: py.plan_year_id,
                                  year: py.year,
                                })
                              }
                              data-testid={`assoc-planyears-reactivate-${py.year}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 border-2 border-navy rounded text-[11px] font-semibold text-navy hover:bg-navy hover:text-white transition-colors"
                              style={{
                                boxShadow: "0 0 0 2px #EA6A4722",
                              }}
                            >
                              <RotateCcw size={11} />
                              Reactivate
                            </button>
                          ) : (
                            <span className="text-slate/50 text-[11px]">
                              &mdash;
                            </span>
                          )}
                          {/* Slice 6 (Dave 2026-03-09 · Severed Arch) —
                              Delete gate:
                                * Platform Admin can delete any plan year
                                  (including the active one — auto-swaps
                                  the successor to active).
                                * Firm Admin / Association Admin (i.e.
                                  `canEdit`) can delete ONLY archived
                                  plan years.
                                * Hidden entirely when there's only 1
                                  plan year (backend refuses too). */}
                          {rows.length > 1 && (isPlatform || (canEdit && !isActive)) && (
                            <button
                              onClick={() =>
                                setConfirmDeletePy({
                                  plan_year_id: py.plan_year_id,
                                  year: py.year,
                                  isActive,
                                })
                              }
                              data-testid={`assoc-planyears-delete-${py.year}`}
                              title={
                                isActive
                                  ? "Hard delete this ACTIVE plan year (Platform Admin only)"
                                  : "Hard delete this archived plan year"
                              }
                              className="p-1 text-slate hover:text-coral hover:bg-coral/10 rounded transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      {confirmPy && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(15,42,68,0.55)" }}
          onClick={() => !reactivating && setConfirmPy(null)}
          data-testid="assoc-planyears-confirm"
        >
          <div
            className="bg-white border-2 border-navy rounded-[6px] w-[420px] max-w-[92vw] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-navy text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] font-bold">
              Reactivate Plan Year
            </div>
            <div className="p-4 text-[12.5px] text-slate leading-relaxed">
              <p className="mb-2 text-navy font-semibold text-[13.5px]">
                Reactivate Plan Year {confirmPy.year}?
              </p>
              <p className="mb-2">
                The currently active plan year (
                <b className="text-navy font-mono">{activeYear ?? "\u2014"}</b>)
                will be archived in its place. All its data stays
                preserved and can be reactivated again at any time.
              </p>
              <p className="text-[11.5px] text-slate/80">
                Note: edits you make on {confirmPy.year} after
                reactivation do <b>not</b> propagate to other years.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-rule px-4 py-2.5">
              <button
                onClick={() => setConfirmPy(null)}
                disabled={reactivating}
                data-testid="assoc-planyears-confirm-cancel"
                className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
              >
                Cancel
              </button>
              <button
                onClick={doReactivate}
                disabled={reactivating}
                data-testid="assoc-planyears-confirm-yes"
                className="px-3 py-1.5 text-[12px] font-semibold text-white bg-navy border-2 border-navy rounded hover:bg-navy/90 disabled:opacity-50"
                style={{ boxShadow: "0 0 0 2px #EA6A4744" }}
              >
                {reactivating ? "Reactivating\u2026" : "Confirm & Reactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 2026-02-23 (Dave) — Platform-Admin-only Hard Delete confirm.
          Same layout as Reactivate for consistency, coral CTA to
          telegraph the destructive nature. */}
      {confirmDeletePy && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(15,42,68,0.55)" }}
          onClick={() => !deleting && setConfirmDeletePy(null)}
          data-testid="assoc-planyears-delete-confirm"
        >
          <div
            className="bg-white border-2 border-coral rounded-[6px] w-[460px] max-w-[92vw] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-coral text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] font-bold">
              Hard Delete Plan Year
            </div>
            <div className="p-4 text-[12.5px] text-slate leading-relaxed">
              <p className="mb-2 text-navy font-semibold text-[13.5px]">
                Delete Plan Year {confirmDeletePy.year}?
              </p>
              <p className="mb-2">
                This <b className="text-coral">permanently removes</b>{" "}
                the plan year and every scenario, element line item, and
                override attached to it. This cannot be undone.
              </p>
              {confirmDeletePy.isActive && rows.length > 1 && (
                <p className="mb-2">
                  Because this is the currently active plan year, the
                  most recent remaining plan year will be promoted to
                  active automatically.
                </p>
              )}
              <p className="text-[11.5px] text-slate/80">
                Platform-Administrator-only. Intended for testing /
                demonstration or an immediate undo of a mistakenly
                commissioned plan year.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-rule px-4 py-2.5">
              <button
                onClick={() => setConfirmDeletePy(null)}
                disabled={deleting}
                data-testid="assoc-planyears-delete-confirm-cancel"
                className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                data-testid="assoc-planyears-delete-confirm-yes"
                className="px-3 py-1.5 text-[12px] font-semibold text-white bg-coral border-2 border-coral rounded hover:bg-coral/90 disabled:opacity-50"
              >
                {deleting ? "Deleting\u2026" : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
